import React, { ReactNode, useEffect, useState } from "react";

import Box from "veneer/Box";
import Button from "veneer/Button";
import { JollyRancherChip } from "veneer/Chip";
import DataTable, { createColumnHelper, createFilter } from "veneer/DataTable";
import Stack from "veneer/Stack";

import DeviceInternalsConstants from "cortex/ui/src/fleet/constant/deviceInternals";
import { Device } from "cortex/ui/src/fleet/view/device/model";
import { useAppDispatch } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import { GET_INTERNAL_STATE_DEVICE } from "cortex/ui/src/redux-store/devices";
import { JsonViewer } from "cortex/ui/src/ui/json-viewer";
import {
  type ResponseLike,
  getErrorMessage,
} from "cortex/ui/src/util/response";
import { Panel } from "cortex/ui/src/view/layout";

type Description =
  | string
  | number
  | boolean
  | null
  | object
  | { [key: string]: Description }
  | ReadonlyArray<Description>;

type InternalStateTableRow = {
  field: string;
  description: Description;
  searchText: string;
};

type InternalStateRecord = Record<string, Description>;
type InternalStateResponse = {
  data?: {
    data?: {
      data?: InternalStateRecord;
    };
  };
};

const filter = createFilter<InternalStateTableRow>("field", "searchText");

const column = createColumnHelper<InternalStateTableRow>();

const columns = [
  column.accessor("field", {
    header: "Field",
    cell: (prop) => formatField(prop.getValue(), prop.row.original),
    sortingFn: "alphanumeric",
  }),
  column.accessor("description", {
    header: "Description",
    cell: (prop) => {
      const field = prop.row.original.field;
      if (!field) {
        return "";
      }
      return formatDescription(field);
    },
    enableSorting: false,
  }),
];

function buildSearchText(desc: Description): string {
  const parts: string[] = [];

  const visit = (v: Description): void => {
    if (v === null || v === undefined) return;

    switch (typeof v) {
      case "string":
      case "number":
      case "boolean":
        parts.push(String(v));
        return;
      case "object":
        if (Array.isArray(v)) {
          for (const item of v) visit(item);
        } else {
          for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
            parts.push(k); // include keys in search
            visit(val as Description);
          }
        }
        return;
      default:
        return;
    }
  };

  visit(desc);

  const text = parts.join(" ").toLowerCase();
  return text.length > 50000 ? text.slice(0, 50000) : text;
}

interface InternalStateTableProps {
  device: Device;
}

export default function InternalStateTable({
  device,
}: InternalStateTableProps) {
  const dispatch = useAppDispatch();
  const [refreshing, setRefreshing] = useState(true);
  const [rows, setRows] = useState<ReadonlyArray<InternalStateTableRow>>([]);

  useEffect(() => {
    if (!refreshing) return;

    let cancelled = false;
    (async () => {
      const deviceId = device.id;
      let internalState: InternalStateRecord = {};
      if (!deviceId) {
        console.error("Device ID is undefined");
        return;
      }

      try {
        const response = (await createPromiseApiAction(
          dispatch,
          GET_INTERNAL_STATE_DEVICE,
          [deviceId],
        )) as InternalStateResponse;
        internalState = response?.data?.data?.data ?? {};
        if (internalState && typeof internalState === "object") {
          const newRows: InternalStateTableRow[] = Object.entries(
            internalState,
          ).map(([key, value]) => ({
            field: key,
            description: value as Description,
            searchText: buildSearchText(value as Description),
          }));
          setRows(newRows);
        }
      } catch (err: unknown) {
        internalState = {
          error: "Failed to retrieve device internal state.",
          server_reason: getErrorMessage(err as Error | ResponseLike | null),
        };
      } finally {
        if (cancelled) return;
        const newRows: InternalStateTableRow[] = Object.entries(
          internalState,
        ).map(([key, value]) => ({
          field: key,
          description: value as Description,
          searchText: buildSearchText(value as Description),
        }));
        setRows(newRows);
        setRefreshing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [device?.id, dispatch, refreshing]);

  return (
    <Panel p={1}>
      <Box mx={-1}>
        <DataTable
          columns={columns}
          filter={filter}
          initialPageSize={50}
          initialSort={{ id: "field", desc: false }}
          primaryActions={
            <Button
              disabled={refreshing}
              icon="download"
              loading={refreshing}
              onClick={() => setRefreshing(true)}
            >
              Retrieve State
            </Button>
          }
          renderExpandedRow={(row) => {
            const value = row.description;
            return isJsonLike(value) ? (
              <JsonViewer value={value as object} />
            ) : (
              <code>{value === null ? "null" : String(value)}</code>
            );
          }}
          rows={rows}
        />
      </Box>
    </Panel>
  );
}

const isJsonLike = (v: Description): v is object | ReadonlyArray<Description> =>
  (typeof v === "object" && v !== null) || Array.isArray(v);

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

function getItemCount(fieldVal: Description) {
  if (Array.isArray(fieldVal)) {
    return fieldVal.length > 0 ? fieldVal.length : null;
  } else if (isPlainObject(fieldVal)) {
    const count = Object.keys(fieldVal).length;
    return count > 0 ? count : null;
  }
  return null;
}

const formatField = (field: string, row: InternalStateTableRow) => {
  const itemCount = getItemCount(row.description);
  return (
    <div className="formatted-field">
      <Stack alignItems="center" direction="row" key={field} spacing={1}>
        <span> {field} </span>
        {itemCount && (
          <JollyRancherChip color="gray" label={`${itemCount} items`} />
        )}
      </Stack>
    </div>
  );
};

export const formatDescription = (field: string) => {
  const messages = DeviceInternalsConstants.messages as Record<
    string,
    ReactNode
  >;

  // Exact match first
  if (field in messages) {
    return messages[field];
  }

  // Regex match fallback
  for (const key of Object.keys(messages)) {
    if (new RegExp(key).test(field)) {
      return messages[key];
    }
  }

  return null;
};
